if (!customElements.get('kit-builder')) {
  customElements.define(
    'kit-builder',
    class KitBuilder extends HTMLElement {
      constructor() {
        super();
        this.checkoutButton = this.querySelector('[data-kit-checkout]');
        this.mobileCheckoutButton = this.querySelector('[data-kit-checkout-mobile]');
        this.checkoutLabels = Array.from(this.querySelectorAll('[data-kit-checkout-label], [data-kit-checkout-label-mobile]'));
        this.summaryList = this.querySelector('[data-kit-summary-list]');
        this.summaryTotal = this.querySelector('[data-kit-summary-total]');
        this.summaryCompare = this.querySelector('[data-kit-summary-compare]');
        this.summaryCompareWrap = this.querySelector('[data-kit-summary-compare-wrap]');
        this.summarySavings = this.querySelector('[data-kit-summary-savings]');
        this.summaryDiscount = this.querySelector('[data-kit-summary-discount]');
        this.savingsPanel = this.querySelector('[data-kit-savings-panel]');
        this.savingsEyebrow = this.querySelector('[data-kit-savings-eyebrow]');
        this.savingsLabel = this.querySelector('[data-kit-savings-label]');
        this.savingsAmount = this.querySelector('[data-kit-savings-amount]');
        this.upsellEl = this.querySelector('[data-kit-upsell]');
        this.mobileTotal = this.querySelector('[data-kit-mobile-total]');
        this.mobileCount = this.querySelector('[data-kit-mobile-count]');
        this.mobileSaving = this.querySelector('[data-kit-mobile-saving]');
        this.mobileCompare = this.querySelector('[data-kit-mobile-compare]');
        this.addonCountEl = this.querySelector('[data-kit-addon-count]');
        this.progressEl = this.querySelector('[data-kit-progress]');
        this.progressFill = this.querySelector('[data-kit-progress-fill]');
        this.errorEl = this.querySelector('[data-kit-error]');
        this.moneyFormat = this.decodeHtml(this.dataset.moneyFormat || '');
        this.totalAddons = parseInt(this.dataset.totalAddons, 10) || 0;
        this.defaultAddonDiscount = parseInt(this.dataset.defaultAddonDiscount, 10) || 0;
        this.defaultCheckoutLabel = this.decodeHtml(this.dataset.checkoutLabel || '');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.strings = this.loadStrings();
        this.lastSavingsKey = '';
      }

      loadStrings() {
        const defaults = {
          addonCount: '__COUNT__ add-ons selected',
          progress: '__SELECTED__ of __TOTAL__ add-ons selected',
          add: 'Tap to add',
          added: 'Added to kit',
          error: 'Could not add kit to cart. Please try again.',
          savings: 'You are saving __AMOUNT__',
          kitDiscount: '__PERCENT__% off this kit',
          savingsEyebrow: 'Kit discount unlocked',
          savingsAmountLabel: 'You are saving',
          upsellStart: '',
          upsellNext: '',
          upsellMax: '',
          addonReward: '+__PERCENT__% off kit',
          addonRewardActive: '__PERCENT__% kit discount active',
          freeWithComplete: 'FREE with complete kit',
          freeUnlocked: 'Free with complete kit',
          freePrice: 'Free',
          upsellFree: 'Select every add-on to unlock a free part',
          checkoutSavings: 'Add kit and save __AMOUNT__',
          mobileSaving: 'Saving __AMOUNT__',
          checkout: this.defaultCheckoutLabel,
        };

        const script = this.querySelector('[data-kit-builder-strings]');
        if (!script) return defaults;

        try {
          const parsed = JSON.parse(script.textContent);
          const strings = { ...defaults };
          Object.keys(parsed).forEach((key) => {
            strings[key] = this.decodeHtml(String(parsed[key] ?? ''));
          });
          strings.checkout = this.defaultCheckoutLabel;
          return strings;
        } catch (error) {
          return defaults;
        }
      }

      decodeHtml(value) {
        if (!value) return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = value;
        return textarea.value;
      }

      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-kit-item]'));
        this.bindEvents();
        this.items.forEach((item) => {
          this.syncItemState(item);
          this.updateItemPriceDisplay(item);
        });
        this.updateSummary();
      }

      bindEvents() {
        this.addEventListener('click', (event) => {
          const card = event.target.closest('[data-kit-card]');
          if (!card) return;
          if (event.target.closest('a, select, label, input, .select')) return;

          const item = card.closest('[data-kit-item]');
          const toggle = item?.querySelector('[data-kit-toggle]');
          if (!toggle || toggle.disabled) return;

          toggle.checked = !toggle.checked;
          toggle.dispatchEvent(new Event('change', { bubbles: true }));
        });

        this.addEventListener('change', (event) => {
          const item = event.target.closest('[data-kit-item]');
          if (!item) return;

          if (event.target.matches('[data-kit-toggle]')) {
            this.onToggleChange(item);
          } else if (event.target.matches('[data-kit-variant-select]')) {
            this.onVariantChange(item, event.target);
          }
        });

        const onCheckout = this.onCheckout.bind(this);
        this.checkoutButton?.addEventListener('click', onCheckout);
        this.mobileCheckoutButton?.addEventListener('click', onCheckout);
      }

      isItemSelected(item) {
        if (item.dataset.itemType === 'base') return item.dataset.available === 'true';
        const toggle = item.querySelector('[data-kit-toggle]');
        return Boolean(toggle?.checked) && item.dataset.available === 'true';
      }

      areAllAddonsSelected() {
        const addons = this.items.filter(
          (item) => item.dataset.itemType === 'addon' && item.dataset.available === 'true'
        );
        if (!addons.length) return false;
        return addons.every((item) => this.isItemSelected(item));
      }

      isItemFree(item) {
        return (
          item?.dataset.freeWhenComplete === 'true' &&
          this.areAllAddonsSelected() &&
          this.isItemSelected(item)
        );
      }

      getItemDiscountPercent(item) {
        if (!item || item.dataset.itemType !== 'addon') return 0;
        if (this.isItemFree(item)) return 0;
        const raw = parseInt(item.dataset.discountPercent, 10);
        if (Number.isFinite(raw)) return Math.max(0, raw);
        return Math.max(0, this.defaultAddonDiscount);
      }

      getKitDiscountPercent() {
        const total = this.items
          .filter((item) => item.dataset.itemType === 'addon' && this.isItemSelected(item))
          .reduce((sum, item) => sum + this.getItemDiscountPercent(item), 0);
        return Math.min(100, total);
      }

      getNextAddonDiscount() {
        const next = this.items.find(
          (item) =>
            item.dataset.itemType === 'addon' &&
            !this.isItemSelected(item) &&
            item.dataset.available === 'true'
        );
        return next ? this.getItemDiscountPercent(next) : 0;
      }

      hasFreeWhenCompleteReward() {
        return this.items.some((item) => item.dataset.freeWhenComplete === 'true');
      }

      hasAnyAddonDiscount() {
        return (
          this.hasFreeWhenCompleteReward() ||
          this.items.some(
            (item) => item.dataset.itemType === 'addon' && this.getItemDiscountPercent(item) > 0
          )
        );
      }

      getKitTotal(chargeTotal, discountPercent) {
        const percent = discountPercent ?? this.getKitDiscountPercent();
        if (percent <= 0) return chargeTotal;
        return Math.round((chargeTotal * (100 - percent)) / 100);
      }

      onToggleChange(item) {
        this.syncItemState(item);
        this.updateSummary();
      }

      onVariantChange(item, select) {
        const option = select.selectedOptions[0];
        if (!option) return;

        item.dataset.variantId = option.value;
        item.dataset.price = option.dataset.price;
        item.dataset.available = option.dataset.available;

        this.updateItemPriceDisplay(item);
        this.updateItemImage(item, option);

        const soldOutEl = item.querySelector('[data-kit-sold-out]');
        const toggle = item.querySelector('[data-kit-toggle]');
        const isAvailable = option.dataset.available === 'true';

        soldOutEl?.classList.toggle('hidden', isAvailable);

        if (toggle) {
          toggle.disabled = !isAvailable;
          if (!isAvailable) toggle.checked = false;
        }

        this.syncItemState(item);
        this.updateSummary();
      }

      updateItemImage(item, option) {
        const imageEl = item.querySelector('[data-kit-item-image]');
        if (!imageEl || !option?.dataset.imageSrc) return;

        imageEl.src = option.dataset.imageSrc;
        if (option.dataset.imageSrcset) {
          imageEl.srcset = option.dataset.imageSrcset;
        }
        if (option.dataset.imageAlt) {
          imageEl.alt = option.dataset.imageAlt;
        }
      }

      updateItemPriceDisplay(item) {
        const original = parseInt(item.dataset.price, 10) || 0;
        const isFree = this.isItemFree(item);
        const priceRoot = item.querySelector('[data-kit-item-price]');
        const regularEl = priceRoot?.querySelector('.price-item--regular, .price-item--sale');
        const freeEl = item.querySelector('[data-kit-free-price]');
        const priceContainer = priceRoot?.querySelector('.price');

        if (regularEl) {
          regularEl.textContent = this.formatMoney(original);
        }

        freeEl?.classList.toggle('hidden', !isFree);
        priceContainer?.classList.toggle('kit-builder__price--struck', isFree);
        item.classList.toggle('kit-builder__item--free', isFree);
      }

      syncItemState(item) {
        const selected = this.isItemSelected(item);
        const variantWrapper = item.querySelector('[data-kit-variant-wrapper]');
        const select = item.querySelector('[data-kit-variant-select]');
        const hint = item.querySelector('[data-kit-hint]');

        if (variantWrapper && item.dataset.itemType === 'addon') {
          variantWrapper.classList.toggle('kit-builder__variant--hidden', !selected);
        }

        if (select) {
          const enableSelect = selected || item.dataset.itemType === 'base';
          select.disabled = !enableSelect;
        }

        if (hint && this.strings.add && this.strings.added) {
          hint.textContent = selected ? this.strings.added : this.strings.add;
        }

        const rewardBadge = item.querySelector('[data-kit-reward-badge]');
        if (rewardBadge) {
          if (item.dataset.freeWhenComplete === 'true') {
            const unlocked = this.isItemFree(item);
            rewardBadge.textContent = unlocked
              ? this.strings.freeUnlocked
              : this.strings.freeWithComplete;
            rewardBadge.classList.toggle('kit-builder__reward-badge--active', unlocked);
            rewardBadge.classList.toggle('kit-builder__reward-badge--free', true);
            rewardBadge.classList.remove('hidden');
          } else {
            const percentValue = this.getItemDiscountPercent(item);
            if (percentValue > 0) {
              const percent = String(percentValue);
              rewardBadge.textContent = selected
                ? this.strings.addonRewardActive.replace('__PERCENT__', percent)
                : this.strings.addonReward.replace('__PERCENT__', percent);
              rewardBadge.classList.toggle('kit-builder__reward-badge--active', selected);
              rewardBadge.classList.remove('hidden');
            } else {
              rewardBadge.classList.add('hidden');
            }
          }
        }

        this.updateItemPriceDisplay(item);
        item.classList.toggle('kit-builder__item--selected', selected);
      }

      getSelectedItems() {
        return this.items
          .filter((item) => this.isItemSelected(item))
          .map((item) => {
            const select = item.querySelector('[data-kit-variant-select]');
            const option = select?.selectedOptions[0];
            const title =
              item.dataset.title ||
              item.querySelector('.kit-builder__item-title')?.textContent?.trim() ||
              '';
            const variantTitle = option?.dataset.title || '';
            const hasOnlyDefault = !select;
            const originalPrice = parseInt(item.dataset.price, 10) || 0;
            const isFree = this.isItemFree(item);

            return {
              variantId: parseInt(item.dataset.variantId, 10),
              price: originalPrice,
              chargePrice: isFree ? 0 : originalPrice,
              isFree,
              title,
              variantTitle: hasOnlyDefault ? '' : variantTitle,
              type: item.dataset.itemType,
            };
          });
      }

      updateSummary() {
        // Refresh free/price state on every addon before totaling
        this.items.forEach((item) => {
          if (item.dataset.itemType === 'addon') this.syncItemState(item);
        });

        const selected = this.getSelectedItems();
        const addonCount = selected.filter((item) => item.type === 'addon').length;
        const compareTotal = selected.reduce((sum, item) => sum + item.price, 0);
        const chargeTotal = selected.reduce((sum, item) => sum + item.chargePrice, 0);
        const kitDiscountPercent = this.getKitDiscountPercent();
        const subtotal = this.getKitTotal(chargeTotal, kitDiscountPercent);
        const savings = compareTotal - subtotal;
        const formattedSavings = this.formatMoney(savings);
        const formattedTotal = this.formatMoney(subtotal);
        const formattedCompare = this.formatMoney(compareTotal);

        if (this.summaryList) {
          this.summaryList.innerHTML = selected
            .map(
              (item) => `
              <li class="kit-builder__summary-line${item.isFree ? ' kit-builder__summary-line--free' : ''}">
                <div>
                  <p class="kit-builder__summary-line-title">${this.escapeHtml(item.title)}</p>
                  ${
                    item.variantTitle
                      ? `<p class="kit-builder__summary-line-variant">${this.escapeHtml(item.variantTitle)}</p>`
                      : ''
                  }
                </div>
                <span class="kit-builder__summary-line-price">
                  ${
                    item.isFree
                      ? `<s class="kit-builder__summary-line-compare">${this.formatMoney(item.price)}</s> ${this.escapeHtml(this.strings.freePrice)}`
                      : this.formatMoney(item.price)
                  }
                </span>
              </li>`
            )
            .join('');
        }

        if (this.summaryTotal) this.summaryTotal.textContent = formattedTotal;
        if (this.mobileTotal) this.mobileTotal.textContent = formattedTotal;

        if (this.summaryCompare) {
          this.summaryCompare.textContent = formattedCompare;
        }
        this.summaryCompareWrap?.classList.toggle('hidden', savings <= 0);

        if (this.mobileCompare) {
          this.mobileCompare.textContent = formattedCompare;
          this.mobileCompare.classList.toggle('hidden', savings <= 0);
        }

        this.updateSavingsPanel(savings, kitDiscountPercent, formattedSavings);
        this.updateUpsell(addonCount, kitDiscountPercent);
        this.updateCheckoutLabels(savings, formattedSavings);

        if (this.summarySavings) {
          this.summarySavings.textContent =
            savings > 0 && this.strings.savings
              ? this.strings.savings.replace('__AMOUNT__', formattedSavings)
              : '';
        }

        if (this.mobileSaving) {
          if (savings > 0 && this.strings.mobileSaving) {
            this.mobileSaving.textContent = this.strings.mobileSaving.replace(
              '__AMOUNT__',
              formattedSavings
            );
            this.mobileSaving.classList.remove('hidden');
          } else {
            this.mobileSaving.textContent = '';
            this.mobileSaving.classList.add('hidden');
          }
        }

        this.classList.toggle('kit-builder--has-savings', savings > 0);

        const countText =
          this.strings.addonCount?.replace('__COUNT__', String(addonCount)) || `${addonCount}`;
        if (this.addonCountEl) this.addonCountEl.textContent = countText;
        if (this.mobileCount) this.mobileCount.textContent = countText;

        if (this.progressEl && this.strings.progress) {
          this.progressEl.textContent = this.strings.progress
            .replace('__SELECTED__', String(addonCount))
            .replace('__TOTAL__', String(this.totalAddons));
        }

        if (this.progressFill && this.totalAddons > 0) {
          const pct = Math.round((addonCount / this.totalAddons) * 100);
          this.progressFill.style.width = `${pct}%`;
        }

        const hasBase = this.items.some((item) => item.dataset.itemType === 'base');
        const baseSelected = this.items.some(
          (item) => item.dataset.itemType === 'base' && this.isItemSelected(item)
        );
        const canCheckout =
          hasBase && baseSelected && selected.length > 0 && selected.every((item) => item.variantId);

        this.checkoutButton?.toggleAttribute('disabled', !canCheckout);
        this.mobileCheckoutButton?.toggleAttribute('disabled', !canCheckout);
      }

      updateSavingsPanel(savings, kitDiscountPercent, formattedSavings) {
        if (!this.savingsPanel) return;

        if (savings > 0) {
          if (this.savingsEyebrow) {
            this.savingsEyebrow.textContent = this.strings.savingsEyebrow;
          }
          if (this.savingsLabel) {
            this.savingsLabel.textContent = this.strings.savingsAmountLabel;
          }
          if (this.savingsAmount) {
            this.savingsAmount.textContent = formattedSavings;
          }
          if (this.summaryDiscount && this.strings.kitDiscount) {
            if (kitDiscountPercent > 0) {
              this.summaryDiscount.textContent = this.strings.kitDiscount.replace(
                '__PERCENT__',
                String(kitDiscountPercent)
              );
              this.summaryDiscount.classList.remove('hidden');
            } else if (this.areAllAddonsSelected() && this.hasFreeWhenCompleteReward()) {
              this.summaryDiscount.textContent = this.strings.freeUnlocked;
              this.summaryDiscount.classList.remove('hidden');
            } else {
              this.summaryDiscount.textContent = '';
              this.summaryDiscount.classList.add('hidden');
            }
          }

          this.savingsPanel.classList.remove('hidden');

          const savingsKey = `${kitDiscountPercent}-${savings}`;
          if (savingsKey !== this.lastSavingsKey) {
            this.savingsPanel.classList.remove('kit-builder__savings-panel--pulse');
            // Force reflow so the pulse animation can replay
            void this.savingsPanel.offsetWidth;
            this.savingsPanel.classList.add('kit-builder__savings-panel--pulse');
            this.lastSavingsKey = savingsKey;
          }
        } else {
          this.savingsPanel.classList.add('hidden');
          this.savingsPanel.classList.remove('kit-builder__savings-panel--pulse');
          this.lastSavingsKey = '';
          if (this.savingsAmount) this.savingsAmount.textContent = '';
          if (this.summaryDiscount) this.summaryDiscount.textContent = '';
        }
      }

      updateUpsell(addonCount, kitDiscountPercent) {
        if (!this.upsellEl || !this.hasAnyAddonDiscount()) return;

        const complete = addonCount >= this.totalAddons && this.areAllAddonsSelected();
        const hasFreeReward = this.hasFreeWhenCompleteReward();
        let message = '';

        if (complete && hasFreeReward && this.strings.upsellMax) {
          message = this.strings.upsellMax;
        } else if (!complete && hasFreeReward && this.strings.upsellFree) {
          message = this.strings.upsellFree;
        } else if (addonCount <= 0 && this.strings.upsellStart) {
          const startPercent = this.getNextAddonDiscount() || this.defaultAddonDiscount;
          message = this.strings.upsellStart.replace('__PERCENT__', String(startPercent));
        } else if (addonCount < this.totalAddons && this.strings.upsellNext) {
          const nextPercent = Math.min(100, kitDiscountPercent + this.getNextAddonDiscount());
          message = this.strings.upsellNext.replace('__PERCENT__', String(nextPercent));
        } else if (complete && kitDiscountPercent > 0 && this.strings.upsellMax) {
          message = this.strings.upsellMax;
        }

        this.upsellEl.textContent = message;
        this.upsellEl.classList.toggle('hidden', !message);
        this.upsellEl.classList.toggle(
          'kit-builder__upsell--max',
          complete && (kitDiscountPercent > 0 || hasFreeReward)
        );
      }

      updateCheckoutLabels(savings, formattedSavings) {
        const label =
          savings > 0 && this.strings.checkoutSavings
            ? this.strings.checkoutSavings.replace('__AMOUNT__', formattedSavings)
            : this.defaultCheckoutLabel;

        this.checkoutLabels.forEach((el) => {
          el.textContent = label;
        });
      }

      formatMoney(cents) {
        const amount = Number(cents) || 0;
        let formatted = '';

        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          formatted = Shopify.formatMoney(amount, this.moneyFormat || undefined);
        } else if (this.moneyFormat) {
          const value = (amount / 100).toFixed(2);
          formatted = this.moneyFormat
            .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amount / 100).toString())
            .replace(/\{\{\s*amount\s*\}\}/g, value);
        } else {
          formatted = (amount / 100).toFixed(2);
        }

        return this.decodeHtml(formatted).replace(/<\/?[^>]+(>|$)/g, '').trim();
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      setError(message = '') {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.toggle('hidden', !message);
      }

      setLoading(loading) {
        [this.checkoutButton, this.mobileCheckoutButton].forEach((button) => {
          if (!button) return;
          button.classList.toggle('loading', loading);
          button.toggleAttribute('disabled', loading);
          button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
        });
      }

      async onCheckout(event) {
        event.preventDefault();
        const selected = this.getSelectedItems();
        if (!selected.length) return;

        this.setError('');
        this.setLoading(true);

        const items = selected.map((item) => ({ id: item.variantId, quantity: 1 }));

        try {
          await this.addToCart(items);
        } catch (error) {
          if (items.length) {
            window.location = this.buildPermalink(items);
            return;
          }
          this.setError(error.message || this.strings.error);
          this.setLoading(false);
        }
      }

      async addToCart(items) {
        const sections =
          this.cart?.getSectionsToRender?.().map((section) => section.id) || ['cart-icon-bubble'];

        const body = JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_add_url}`, {
          ...fetchConfig('javascript'),
          body,
        });

        const data = await response.json();

        if (data.status) {
          throw new Error(data.description || data.message || this.strings.error);
        }

        if (this.cart?.renderContents && data.key) {
          this.cart.setActiveElement?.(document.activeElement);
          this.cart.renderContents(data);
          this.setLoading(false);
          return;
        }

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kit-builder', cartData: data });
        }

        window.location = window.routes.cart_url;
      }

      buildPermalink(items) {
        return `${window.routes.cart_url}/${items.map((item) => `${item.id}:1`).join(',')}`;
      }
    }
  );
}
